import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import DigitalIDCard from '../../components/DigitalIDCard/DigitalIDCard';
import styles from './ShareableId.module.css';
import { usePageSEO } from '../../hooks/usePageSEO';

const ShareableId: React.FC = () => {
  const { userId } = useParams<{ userId: string }>();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [targetUser, setTargetUser] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [authInitialized, setAuthInitialized] = useState(false);

  // SEO for the shareable ID page
  usePageSEO({
    title: targetUser ? `${targetUser.email.split('@')[0]}'s Digital Dietary ID | PlatefullAI` : 'Digital Dietary ID | PlatefullAI',
    description: targetUser ? `View ${targetUser.email.split('@')[0]}'s dietary preferences and health information.` : 'View digital dietary identity information.',
    keywords: 'digital dietary identity, dietary preferences, food allergies, dietary restrictions, share dietary info',
    canonicalUrl: `https://platefullai.com/id/${userId}`,
    imageUrl: `${window.location.origin}/images/DigitalIDCardThumbnail.svg`
  });

  // Track when auth context has been initialized
  useEffect(() => {
    // Give auth context time to initialize
    const timer = setTimeout(() => {
      setAuthInitialized(true);
    }, 200);

    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const fetchUserData = async () => {
      if (!userId) {
        setError('Invalid user ID');
        setLoading(false);
        return;
      }

      // Wait for auth to be initialized
      if (!authInitialized) {
        return;
      }

      try {
        // If it's the current user, use their data directly
        if (user && user.id === userId) {
          setTargetUser(user);
          setLoading(false);
          return;
        }

        // For other users, we'd need an API endpoint to fetch public user data
        // For now, we'll show an error for other users
        setError('User data not available');
        setLoading(false);
      } catch (err) {
        setError('Failed to load user data');
        setLoading(false);
      }
    };

    fetchUserData();
  }, [userId, user, authInitialized]);

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.loading}>
          <div className={styles.spinner}></div>
          <p>Loading dietary ID...</p>
        </div>
      </div>
    );
  }

  if (error || !targetUser) {
    return (
      <div className={styles.container}>
        <div className={styles.error}>
          <h2>Dietary ID Not Found</h2>
          <p>{error || 'This dietary ID could not be found.'}</p>
          <button 
            className={styles.backButton}
            onClick={() => navigate('/')}
          >
            Go Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h1>Digital Dietary ID</h1>
        <p className={styles.subtitle}>
          {targetUser.email.split('@')[0]}
        </p>
      </div>
      
      <div className={styles.idCardContainer}>
        <DigitalIDCard showActions={false} />
      </div>

      <div className={styles.footer}>
        <p className={styles.footerText}>
          This dietary ID is powered by <strong>PlatefullAI</strong>
        </p>
        {user && user.id !== userId && (
          <button 
            className={styles.createButton}
            onClick={() => navigate('/')}
          >
            Create Your Own ID
          </button>
        )}
      </div>
    </div>
  );
};

export default ShareableId;
